import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { User, Building2, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { HumaTwinIcon } from '../../components/brand/HumaTwinLogo';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('PERSONAL');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(t('success.welcomeBack'));
      
      if (user.userType === 'PERSONAL') {
        navigate('/personal/twin');
      } else {
        navigate('/enterprise/orgs');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Navy gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700" />
        
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-blue-400 blur-3xl" />
          <div className="absolute bottom-40 right-20 w-80 h-80 rounded-full bg-teal-400 blur-3xl" />
        </div>
        
        {/* Abstract human silhouette (decorative) */}
        <div className="absolute right-0 bottom-0 w-3/4 h-3/4 opacity-20">
          <svg viewBox="0 0 400 500" className="w-full h-full">
            <defs>
              <linearGradient id="silhouetteGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4A6CF7" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#00B4A6" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <ellipse cx="200" cy="100" rx="60" ry="70" fill="url(#silhouetteGrad)" />
            <path d="M100 180 Q200 150 300 180 L320 350 Q200 380 80 350 Z" fill="url(#silhouetteGrad)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Top - Logo */}
          <div className="flex items-center gap-3">
            <HumaTwinIcon size={48} />
            <span className="font-heading text-2xl font-bold">HumaTwin</span>
          </div>
          
          {/* Center - Tagline */}
          <div className="flex-1 flex flex-col justify-center max-w-md">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {t('auth.heroTitle') || 'Your digital representation.'}
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('auth.heroSubtitle') || 'Built from your evidence.'}
            </p>
            <p className="text-gray-400 mt-4">
              {t('auth.heroDesc') || 'A deeper understanding of you. For a more intentional tomorrow.'}
            </p>
          </div>
          
          {/* Bottom - Footer */}
          <div className="text-sm text-gray-400">
            <p>{t('auth.tagline') || 'A more human future, built with you.'}</p>
            <div className="flex items-center gap-4 mt-4 text-xs">
              <Link to="/privacy" className="hover:text-white transition-colors">
                {t('nav.privacy') || 'Privacy'}
              </Link>
              <span>•</span>
              <span>{t('auth.yourData') || 'Your Data'}</span>
              <span>•</span>
              <span>{t('auth.yourControl') || 'Your Control'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden p-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2">
            <HumaTwinIcon size={32} />
            <span className="font-heading text-lg font-bold text-gray-900">HumaTwin</span>
          </div>
          <LanguageSwitcher variant="compact" />
        </div>
        
        {/* Desktop Language Switcher */}
        <div className="hidden lg:flex justify-end p-4">
          <LanguageSwitcher variant="compact" />
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-gray-900">{t('auth.welcomeBack') || 'Welcome back'}</h1>
              <p className="text-gray-500 mt-2">{t('auth.loginSubtitle') || 'Sign in to continue'}</p>
            </div>

            {/* User Type Toggle */}
            <div className="flex p-1 bg-gray-100 rounded-xl">
              <button
                data-testid="personal-login-tab"
                onClick={() => setUserType('PERSONAL')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium transition-all text-sm ${
                  userType === 'PERSONAL'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <User className="w-4 h-4" />
                {t('auth.individual') || 'Individual'}
              </button>
              <button
                data-testid="enterprise-login-tab"
                onClick={() => setUserType('ENTERPRISE')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium transition-all text-sm ${
                  userType === 'ENTERPRISE'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Building2 className="w-4 h-4" />
                {t('auth.enterprise') || 'Enterprise'}
              </button>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  {t('auth.email') || 'Email'}
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder') || 'you@example.com'}
                  required
                  className="h-11 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">
                  {t('auth.password') || 'Password'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    className="h-11 rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                  />
                  <Label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">
                    {t('auth.rememberMe') || 'Remember me'}
                  </Label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:text-primary-hover transition-colors"
                >
                  {t('auth.forgotPassword') || 'Forgot password?'}
                </Link>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full h-11 rounded-xl text-base font-medium bg-primary hover:bg-primary-hover"
                disabled={loading}
              >
                {loading ? t('common.loading') : (t('auth.signIn') || 'Sign in')}
              </Button>
            </form>

            {/* Sign Up Link */}
            <div className="text-center pt-4 border-t border-gray-100">
              <p className="text-gray-600">
                {t('auth.noAccount') || "Don't have an account?"}{' '}
                <Link
                  to="/signup"
                  className="text-primary hover:text-primary-hover font-medium transition-colors"
                  data-testid="signup-link"
                >
                  {t('auth.signupLink') || 'Sign up'}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
